import React from 'react';
import {Box, Typography} from '@mui/material';
import {BuilderPot, BuilderPot_dentistChildren} from '../../../schema-types/BuilderPot';
import {ParentPlanRow} from '../CreateBuilder/BuilderSourceDestination/ParentPlanRow';
import {PlanAccordionShell} from '../CreateBuilder/BuilderSourceDestination/PlanAccordionShell';
import {PlanSelector} from '../CreateBuilder/BuilderSourceDestination/PlanSelector';
import {PlanValidationIcon} from '../CreateBuilder/BuilderSourceDestination/PlanValidationIcon';
import {usePlanValidationLevel} from '../CreateBuilder/BuilderSourceDestination/usePlanValidationLevel';
import {
  getSummary,
  getSummaryParts,
} from '../CreateBuilder/BuilderSourceDestination/builderNaming';
import {BuilderAnnuitySourceForm} from './BuilderAnnuitySourceForm';
import {AnnuitySourceSubplan} from './AnnuitySourceSubplan';
import {getCurrencyFloat} from '../../../utils/currencyFormatter';

interface AnnuitySourceParentProps {
  pot: BuilderPot;
  selectedSourceId: string | undefined;
  handleChange: (id: string) => void;
}

export const AnnuitySourceParent: React.FC<AnnuitySourceParentProps> = ({
  pot,
  selectedSourceId,
  handleChange,
}) => {
  const dentistChildren: BuilderPot_dentistChildren[] = pot.dentistChildren ?? [];
  const hasChildren = dentistChildren.length > 0;
  const isLeafSelected = !hasChildren && selectedSourceId === pot.id;
  const anyChildSelected =
    hasChildren && dentistChildren.some((c) => c.id === selectedSourceId);
  const activeFormikKey = isLeafSelected ? 'annuity' : undefined;
  const {bgClass} = usePlanValidationLevel(activeFormikKey);
  const {label, currentValue} = getSummaryParts(pot);

  if (hasChildren) {
    return (
      <ParentPlanRow
        pot={pot}
        isSelected={anyChildSelected}
        summaryChildren={
          <Box sx={{display: 'flex', alignItems: 'center', flex: 1}}>
            <Typography variant="body1" sx={{fontWeight: 600}}>
              {label}
            </Typography>
            {currentValue && (
              <Typography
                variant="body2"
                sx={{fontWeight: 600, ml: 'auto', whiteSpace: 'nowrap'}}
              >
                {currentValue}
              </Typography>
            )}
          </Box>
        }
      >
        {dentistChildren.map((child) => (
          <AnnuitySourceSubplan
            key={child.id}
            subPlan={child}
            isSelected={selectedSourceId === child.id}
            handleChange={() => handleChange(child.id)}
          />
        ))}
      </ParentPlanRow>
    );
  }

  return (
    <ParentPlanRow pot={pot} isSelected={isLeafSelected}>
      <PlanAccordionShell
        expanded={isLeafSelected}
        isSelected={isLeafSelected}
        isSoleParent
        validationLevel={bgClass}
        summaryChildren={
          <>
            <PlanSelector
              mode="single"
              id={pot.id}
              isSelected={isLeafSelected}
              handleChange={() => handleChange(pot.id)}
            />
            {getSummary(pot)}
          </>
        }
        summaryTrailing={
          activeFormikKey ? (
            <PlanValidationIcon formikKeyPrefix={activeFormikKey} />
          ) : undefined
        }
      >
        <BuilderAnnuitySourceForm
          pot={pot}
          maxAmount={getCurrencyFloat(pot.totalValue)}
        />
      </PlanAccordionShell>
    </ParentPlanRow>
  );
};
